import { Copy } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useGetCatalogComponent } from '@/shared/hooks/api/catalog'
import { V2ComponentRohsBriefing } from '@/v2/components/component-rohs-briefing/V2ComponentRohsBriefing'
import { V2TableLink } from '@/v2/components/V2TableLink'
import { getV2ProductDetailPath } from '@/v2/utils/routes'

function formatProductName(
  name: string | null,
  sku: string | null,
  fallback: string
): string {
  if (name != null && name.trim() !== '') {
    return name.trim()
  }
  if (sku != null && sku.trim() !== '') {
    return sku.trim()
  }
  return fallback
}

function formatProvisionedBecause(
  products: Array<{ name: string }>,
  translate: (key: string, options?: Record<string, unknown>) => string
): string {
  if (products.length === 0) {
    return translate('v2.pages.components.detail.obligations.provisionedIdle')
  }
  const names = products.map((product) => product.name).join(', ')
  return translate('v2.pages.components.detail.obligations.provisionedBecause', {
    products: names,
  })
}

export default function V2ComponentDetailPage() {
  const { t } = useTranslation('common')
  const { componentId } = useParams<{ componentId: string }>()
  const { data: component, isLoading, isError } = useGetCatalogComponent(
    componentId
  )

  async function copySupplierLink(magicLinkUrl: string) {
    await navigator.clipboard.writeText(magicLinkUrl)
    toast.success(t('v2.pages.suppliers.copyInviteLinkSuccess'))
  }

  if (componentId == null || componentId.length === 0) {
    return (
      <p className='text-sm text-muted-foreground'>
        {t('v2.pages.components.detail.loadFailed')}
      </p>
    )
  }

  if (isLoading) {
    return (
      <div className='space-y-4' aria-busy='true'>
        <Skeleton className='h-10 w-64' />
        <Skeleton className='h-32 w-full' />
        <Skeleton className='h-48 w-full' />
      </div>
    )
  }

  if (isError || component == null) {
    return (
      <p className='text-sm text-destructive'>
        {t('v2.pages.components.detail.loadFailed')}
      </p>
    )
  }

  const title =
    component.name != null && component.name.trim() !== ''
      ? component.name.trim()
      : t('v2.pages.components.fallbackName')
  const rohsFile = component.rohsFile
  const canFulfil = rohsFile?.status === 'ACTIVE'
  const provisionedProducts =
    rohsFile?.provisionedBecause.map((product) => ({
      name: product.name,
    })) ?? []

  return (
    <div className='space-y-8'>
      <header className='space-y-2'>
        <h1 className='text-2xl font-semibold tracking-tight'>{title}</h1>
        <p className='text-sm text-muted-foreground'>
          {t('v2.pages.components.detail.meta', {
            sku: component.sku,
            supplier: component.supplier.name,
          })}
        </p>
        <Button
          className='gap-2'
          onClick={() => {
            void copySupplierLink(component.supplier.magicLinkUrl)
          }}
          size='sm'
          type='button'
          variant='outline'
        >
          <Copy className='size-4' />
          {t('v2.pages.suppliers.copyInviteLink')}
        </Button>
      </header>

      <section className='space-y-3'>
        <h2 className='text-lg font-semibold'>
          {t('v2.pages.components.detail.products.heading')}
        </h2>
        {component.products.length === 0 ? (
          <Empty className='border border-dashed bg-muted/20'>
            <EmptyHeader>
              <EmptyTitle>
                {t('v2.pages.components.detail.products.empty.title')}
              </EmptyTitle>
              <EmptyDescription>
                {t('v2.pages.components.detail.products.empty.description')}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t('v2.pages.components.detail.products.columns.product')}
                </TableHead>
                <TableHead>
                  {t('v2.pages.components.detail.products.columns.sku')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {component.products.map((product) => {
                const productName = formatProductName(
                  product.name,
                  product.sku,
                  t('v2.pages.products.fallbackTitle')
                )
                const skuLabel =
                  product.sku != null && product.sku.trim() !== ''
                    ? product.sku.trim()
                    : t('notAvailable')

                return (
                  <TableRow key={product.id}>
                    <TableCell>
                      <V2TableLink to={getV2ProductDetailPath(product.id)}>
                        {productName}
                      </V2TableLink>
                    </TableCell>
                    <TableCell className='text-muted-foreground'>
                      {skuLabel}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </section>

      <section className='space-y-3'>
        {rohsFile == null ? (
          <>
            <h2 className='text-lg font-semibold'>
              {t('v2.pages.components.detail.obligations.heading')}
            </h2>
            <p className='text-sm text-muted-foreground'>
              {t('v2.pages.components.detail.obligations.noFile')}
            </p>
          </>
        ) : (
          <V2ComponentRohsBriefing
            componentId={component.id}
            canFulfil={canFulfil}
            rohsFileSummary={rohsFile}
            supplierMagicLinkUrl={component.supplier.magicLinkUrl}
            provisionedBecauseText={formatProvisionedBecause(
              provisionedProducts,
              t
            )}
          />
        )}
      </section>
    </div>
  )
}
